import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Select,
  MenuItem,
  TablePagination,
  CircularProgress,
  Tooltip,
  InputLabel,
  FormControl,
  FormControlLabel,
  Checkbox,
  Alert,
  IconButton,
  Button,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSnackbar } from "notistack";
import { isAxiosError } from "axios";
import {
  useSyncConfigs,
  useSyncQueueItems,
  useSyncRuns,
  useRequestSyncRunReport,
} from "../../../api/hooks/useSyncConfigs";
import type { SyncConfigDto, SyncQueueDto, SyncRunDto } from "../../../api/types/dtos/sync";
import FarmDetailsModal from "../../../components/modal/FarmDetailsModal";

const statusColor: Record<string, "default" | "success" | "warning" | "error" | "info"> = {
  pending: "warning",
  sent: "success",
  failed: "error",
  idle: "default",
  running: "info",
  completed: "success",
};

const formatDate = (iso: string | null): string => {
  if (!iso) return "\u2014";
  try {
    return new Date(iso).toLocaleString("es-CO", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const shortId = (id: string): string => (id ? id.substring(0, 8) : "\u2014");

const SyncRunReportCell: React.FC<{ run: SyncRunDto }> = ({ run }) => {
  const { enqueueSnackbar } = useSnackbar();
  const requestReport = useRequestSyncRunReport();

  const handleRequest = useCallback(async () => {
    try {
      await requestReport.mutateAsync({ configId: run.syncConfigId, runId: run.id });
      enqueueSnackbar("Reporte solicitado", { variant: "success" });
    } catch {
      enqueueSnackbar("Error al solicitar el reporte", { variant: "error" });
    }
  }, [run.syncConfigId, run.id, requestReport, enqueueSnackbar]);

  const handleDownload = useCallback(async () => {
    if (!run.reportCsvS3Url) {
      enqueueSnackbar("URL de descarga no disponible", { variant: "error" });
      return;
    }
    try {
      const res = await fetch(run.reportCsvS3Url);
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.setAttribute("download", `sync-report-${run.id}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      enqueueSnackbar("Error al descargar el reporte", { variant: "error" });
    }
  }, [run.reportCsvS3Url, run.id, enqueueSnackbar]);

  if (run.totalItems === 0) {
    return (
      <Tooltip title="Sin items para reportar">
        <span>
          <IconButton size="small" disabled>
            <AssessmentIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  if (run.reportCsvStatus === "requested" || run.reportCsvStatus === "in_process") {
    return (
      <Tooltip title="Generando reporte...">
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress size={20} />
        </Box>
      </Tooltip>
    );
  }

  if (run.reportCsvStatus === "available") {
    return (
      <Tooltip title="Descargar CSV">
        <IconButton
          size="small"
          color="primary"
          onClick={handleDownload}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  if (run.reportCsvStatus === "failed") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Tooltip title="El reporte fallo al generarse">
          <ErrorOutlineIcon color="error" fontSize="small" />
        </Tooltip>
        <Tooltip title="Reintentar">
          <IconButton size="small" onClick={handleRequest} disabled={requestReport.isPending}>
            {requestReport.isPending ? (
              <CircularProgress size={18} />
            ) : (
              <RefreshIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Tooltip title="Generar CSV">
      <Button
        size="small"
        variant="outlined"
        startIcon={<AssessmentIcon />}
        onClick={handleRequest}
        disabled={requestReport.isPending}
      >
        Generar CSV
      </Button>
    </Tooltip>
  );
};

export const SyncQueueTab: React.FC = () => {
  const { data: configs, error: configsError } = useSyncConfigs();
  const [configId, setConfigId] = useState<string>("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [farmDetailsOpen, setFarmDetailsOpen] = useState(false);

  const handleOrgClick = useCallback((orgId: string) => {
    setSelectedOrgId(orgId);
    setFarmDetailsOpen(true);
  }, []);

  const handleFarmDetailsClose = useCallback(() => {
    setFarmDetailsOpen(false);
    setSelectedOrgId(null);
  }, []);

  useEffect(() => {
    if (!configId && configs && configs.length > 0) {
      setConfigId(configs[0].id);
    }
  }, [configs, configId]);

  const { data, isLoading, error } = useSyncQueueItems(configId || null, page, rowsPerPage);

  const items: SyncQueueDto[] = data?.content ?? [];
  const total = data?.totalElements ?? 0;

  const isForbidden =
    (isAxiosError(configsError) && configsError.response?.status === 403) ||
    (isAxiosError(error) && error.response?.status === 403);

  if (isForbidden) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }}>
        <Alert severity="warning">No tienes permisos para ver la cola de sincronizacion.</Alert>
      </Paper>
    );
  }

  return (
    <Box>
      <FormControl size="small" sx={{ minWidth: 300, mb: 2 }}>
        <InputLabel>Configuracion</InputLabel>
        <Select
          value={configId}
          label="Configuracion"
          onChange={(e) => {
            setConfigId(e.target.value);
            setPage(0);
          }}
        >
          {(configs ?? []).map((c: SyncConfigDto) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell>Config</TableCell>
              <TableCell>Finca</TableCell>
              <TableCell>Entidad</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="center">Reintentos</TableCell>
              <TableCell>Error</TableCell>
              <TableCell>Encolado</TableCell>
              <TableCell>Procesado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={24} sx={{ my: 2 }} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No hay items en cola.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => {
                const configName =
                  configs?.find((c) => c.id === item.syncConfigId)?.name ??
                  shortId(item.syncConfigId);
                return (
                  <TableRow key={item.id} hover>
                    <TableCell>{configName}</TableCell>
                    <TableCell>
                      <Tooltip title={item.targetOrganizationId}>
                        <Button
                          size="small"
                          onClick={() => handleOrgClick(item.targetOrganizationId)}
                          sx={{ textTransform: "none", minWidth: 0, p: 0, justifyContent: "flex-start" }}
                        >
                          {shortId(item.targetOrganizationId)}
                        </Button>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {item.entityType}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {shortId(item.entityId)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.status}
                        size="small"
                        color={statusColor[item.status] ?? "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {item.attempts}/{item.maxAttempts}
                    </TableCell>
                    <TableCell>
                      {item.lastError ? (
                        <Tooltip title={item.lastError}>
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "block",
                            }}
                          >
                            {item.lastError}
                          </Typography>
                        </Tooltip>
                      ) : (
                        "\u2014"
                      )}
                    </TableCell>
                    <TableCell>{formatDate(item.queuedAt)}</TableCell>
                    <TableCell>{formatDate(item.processedAt)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50]}
        labelRowsPerPage="Filas:"
      />
      {selectedOrgId && (
        <FarmDetailsModal
          open={farmDetailsOpen}
          onClose={handleFarmDetailsClose}
          organizationId={selectedOrgId}
        />
      )}
    </Box>
  );
};

export const SyncRunsTab: React.FC = () => {
  const { data: configs, error: configsError } = useSyncConfigs();
  const [configId, setConfigId] = useState<string>("");
  const [hideIdle, setHideIdle] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  useEffect(() => {
    if (!configId && configs && configs.length > 0) {
      setConfigId(configs[0].id);
    }
  }, [configs, configId]);

  const { data, isLoading, error, refetch } = useSyncRuns(
    configId || null,
    hideIdle,
    page,
    rowsPerPage
  );

  const runs: SyncRunDto[] = data?.content ?? [];
  const total = data?.totalElements ?? 0;

  const hasPendingReport = runs.some(
    (r) => r.reportCsvStatus === "requested" || r.reportCsvStatus === "in_process"
  );

  useEffect(() => {
    if (!hasPendingReport) return;
    const interval = setInterval(() => {
      refetch();
    }, 5000);
    return () => clearInterval(interval);
  }, [hasPendingReport, refetch]);

  const isForbidden =
    (isAxiosError(configsError) && configsError.response?.status === 403) ||
    (isAxiosError(error) && error.response?.status === 403);

  if (isForbidden) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }}>
        <Alert severity="warning">
          No tienes permisos para ver las ejecuciones de sincronizacion.
        </Alert>
      </Paper>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel>Configuracion</InputLabel>
          <Select
            value={configId}
            label="Configuracion"
            onChange={(e) => {
              setConfigId(e.target.value);
              setPage(0);
            }}
          >
            {(configs ?? []).map((c: SyncConfigDto) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={hideIdle}
              onChange={(e) => setHideIdle(e.target.checked)}
            />
          }
          label="Ocultar idle"
        />
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell>Config</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="center">Total</TableCell>
              <TableCell align="center">Exitos</TableCell>
              <TableCell align="center">Fallos</TableCell>
              <TableCell>Inicio</TableCell>
              <TableCell>Fin</TableCell>
              <TableCell>Error</TableCell>
              <TableCell>Reporte</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress size={24} sx={{ my: 2 }} />
                </TableCell>
              </TableRow>
            ) : runs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No hay ejecuciones registradas.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              runs.map((run) => {
                const configName =
                  configs?.find((c) => c.id === run.syncConfigId)?.name ??
                  shortId(run.syncConfigId);
                return (
                  <TableRow key={run.id} hover>
                    <TableCell>{configName}</TableCell>
                    <TableCell>
                      <Chip
                        label={run.status}
                        size="small"
                        color={statusColor[run.status] ?? "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">{run.totalItems}</TableCell>
                    <TableCell align="center">{run.successCount}</TableCell>
                    <TableCell align="center">{run.failureCount}</TableCell>
                    <TableCell>{formatDate(run.startedAt)}</TableCell>
                    <TableCell>{formatDate(run.completedAt)}</TableCell>
                    <TableCell>
                      {run.errorMessage ? (
                        <Tooltip title={run.errorMessage}>
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "block",
                            }}
                          >
                            {run.errorMessage}
                          </Typography>
                        </Tooltip>
                      ) : (
                        "\u2014"
                      )}
                    </TableCell>
                    <TableCell>
                      <SyncRunReportCell run={run} />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50]}
        labelRowsPerPage="Filas:"
      />
    </Box>
  );
};
